import { useState, useEffect, useRef } from "react";
import { useAuth0 } from '@auth0/auth0-react';
import apiClientOC from "icfrontendapiclient";
import { auth0Config } from '../../config/auth0Config';

const CSRF_EXEMPT_PATHS = [
    '/csrf/token',
    '/auth/signin',
    '/auth/signout',
    'auth/resend_verification_email'
];

const useBizApiClient = ({
    name, 
    method, 
    path, 
    defaultValue = null,
    formatResultFunc = (unformattedResult) => unformattedResult,
    preventStateResult = false,
    headers = {}
}) => {
    const [result, setResult] = useState(defaultValue);
    const bizApiClient = apiClientOC.get("BizApiClient");
    const isRegisteredRef = useRef(false);
    const csrfTokenPromiseRef = useRef(null);
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();

    const normalizedMethod = method.toLowerCase();
    if (!isRegisteredRef.current) {
        bizApiClient.registerEndpoint(name, normalizedMethod, path);
        if (['post', 'put', 'patch', 'delete'].includes(normalizedMethod)) {
            bizApiClient.registerEndpoint("getCsrfToken", "get", "/csrf/token");
        }
        isRegisteredRef.current = true;
    }

    useEffect(() => {
        bizApiClient.registerEndpoint(name, normalizedMethod, path);
        if (['post', 'put', 'patch', 'delete'].includes(normalizedMethod)) {
            bizApiClient.registerEndpoint("getCsrfToken", "get", "/csrf/token");
        }
    }, [path, name, normalizedMethod, bizApiClient]);

    const getCsrfToken = async () => {
        if (!csrfTokenPromiseRef.current) {
            csrfTokenPromiseRef.current = bizApiClient.getCsrfToken()
                .then(response => {
                    if (!response?.csrf_token) {
                        throw new Error('CSRF token missing in response');
                    }
                    return response.csrf_token;
                })
                .finally(() => {
                    csrfTokenPromiseRef.current = null;
                });
        }
        return await csrfTokenPromiseRef.current || '';
    };

    const sendRequest = async (payload = {}, queryParams = {}, dynamicHeaders = null) => {
        if (!isRegisteredRef.current) {
            bizApiClient.registerEndpoint(name, normalizedMethod, path);
            if (['post', 'put', 'patch', 'delete'].includes(normalizedMethod)) {
                bizApiClient.registerEndpoint("getCsrfToken", "get", "/csrf/token");
            }
            isRegisteredRef.current = true;
        }

        let requestHeaders = dynamicHeaders || (headers && Object.keys(headers).length > 0 ? headers : {});
        
        if (isAuthenticated) {
            const token = await getAccessTokenSilently({
                audience: auth0Config.authorizationParams.audience,
                scope: 'openid profile email offline_access',
            });
            requestHeaders = {
                ...requestHeaders,
                'Authorization': `Bearer ${token}`
            };
        }
        
        if (typeof bizApiClient[name] !== 'function') {
            throw new Error(`Endpoint "${name}" is not registered. Please ensure the endpoint is registered before calling.`);
        }
        
        const isStateChangeMethod = ['post', 'put', 'patch', 'delete'].includes(normalizedMethod);
        const normalizedPath = path.toLowerCase();
        const isExemptPath = CSRF_EXEMPT_PATHS.some(exemptPath => 
            normalizedPath.includes(exemptPath.toLowerCase())
        );
        
        if (isStateChangeMethod && !isExemptPath) {
            const csrfToken = await getCsrfToken();
            requestHeaders = {
                ...requestHeaders,
                'X-CSRF-Token': csrfToken
            };
        }
        
        let response;
        if (normalizedMethod === 'get') {
            response = Object.keys(requestHeaders).length > 0
                ? await bizApiClient[name](queryParams, requestHeaders)
                : await bizApiClient[name](queryParams);
        } else {
            response = Object.keys(requestHeaders).length > 0
                ? await bizApiClient[name](payload, queryParams, requestHeaders)
                : await bizApiClient[name](payload, queryParams);
        }
        
        const formattedResult = formatResultFunc(response);
        if (!preventStateResult) {
            setResult(formattedResult);
        }
        return formattedResult;
    };
    
    return [sendRequest, result];
};

export { useBizApiClient };

